import React from 'react';
import { aiSuggest } from '../../../api/cmail.api';
import { useAPIErrorHandler } from '../../../components/hooks/useAPIErrorHandler';
import useNotifications from '../../../_lib/notifications/useNotifications';
import type { FormState } from './useEmailForm';
import { stripHtmlTagsPreserveNewlines } from '../utils/htmlContentUtils';
import { normalizeMessageCategoryForAPI } from '../utils/emailTypeUtils';

export interface UseAIAssistProps {
  form: FormState;
  setForm: (form: FormState | ((prev: FormState) => FormState)) => void;
  msgCategory?: string;
  getEmailType: () => 'NEW' | 'REPLY' | 'FORWARD' | 'FOLLOWUP';
  missingFields?: Record<string, any>;
  missingFieldsDescription?: string;
}

export const useAIAssist = ({
  form,
  setForm,
  msgCategory,
  getEmailType,
  missingFields,
  missingFieldsDescription,
}: UseAIAssistProps) => {
  const [aiAssistOpen, setAIAssistOpen] = React.useState(false);
  const [aiLoading, setAILoading] = React.useState(false);
  const [aiSuggestions, setAISuggestions] = React.useState<string[]>([]);
  const [aiRefineMode, setAIRefineMode] = React.useState(false);
  const [aiRefineInput, setAIRefineInput] = React.useState('');
  const [aiRefineTone, setAIRefineTone] = React.useState('Professional');
  const handleAPIError = useAPIErrorHandler();
  const notifications = useNotifications();

  // AI suggestions API call
  const fetchAISuggestions = React.useCallback(
    async ({ subject, body, mode, refine }: any) => {
      try {
        const payload: any = {
          subject,
          body,
          mode,
          emailType: getEmailType(),
          msgCategory: normalizeMessageCategoryForAPI(msgCategory),
          to: form.to,
          ...(mode === 'refine' && refine && { refine }),
        };

        // If we have missing fields data and this is a refine request, include field names as text
        if (
          mode === 'refine' &&
          missingFields &&
          Object.keys(missingFields).length > 0
        ) {
          // Extract field names and format them as text
          const fieldNames = Object.keys(missingFields).map((field) =>
            field.replace(/([A-Z])/g, ' $1').trim()
          );
          const fieldList = fieldNames.join(', ');

          // Create context-aware description based on email type
          const emailType = getEmailType();
          let missingFieldsText = '';

          if (emailType === 'REPLY') {
            missingFieldsText = `Please provide the following information for us to move forward: ${fieldList}. `;
          } else if (emailType === 'FORWARD') {
            missingFieldsText = `I need a formatted email where I can provide the following values: ${fieldList}. `;
          } else {
            missingFieldsText = `Missing fields: ${fieldList}. `;
          }

          // Update the description to include the missing fields as text
          const currentDescription = payload.refine?.description || '';

          payload.refine = {
            description: missingFieldsText + currentDescription,
            tone: 'Professional', // Always use Professional tone for missing fields
          };
        }

        const response = await aiSuggest({ data: payload });
        return response.data.suggestions;
      } catch (error: any) {
        console.error('Error fetching AI suggestions:', error);
        handleAPIError('Failed to fetch AI suggestions')(error as Error);
        return [];
      }
    },
    [
      form.to,
      getEmailType,
      msgCategory,
      missingFields,
      missingFieldsDescription,
      handleAPIError,
    ]
  );

  const handleAIAssist = React.useCallback(async () => {
    try {
      setAIAssistOpen(true);
      setAILoading(true);
      setAISuggestions([]);

      // Clean the body before sending to AI Assist
      const cleanBody = stripHtmlTagsPreserveNewlines(form.body);

      // If we have missing fields data, automatically use refine mode
      if (missingFields && Object.keys(missingFields).length > 0) {
        // Extract field names and format them properly
        const fieldNames = Object.keys(missingFields).map((field) =>
          field.replace(/([A-Z])/g, ' $1').trim()
        );
        const fieldList = fieldNames.join(', ');

        // Create context-aware description based on email type
        const emailType = getEmailType();
        let description = '';

        if (emailType === 'REPLY') {
          description = `Please provide the following information for us to move forward: ${fieldList}. Please format this as a professional request for the missing information, listing each field clearly.`;
        } else if (emailType === 'FORWARD') {
          description = `I need a formatted email where I can provide the following values: ${fieldList}. Please create a professional email template with placeholders for these fields.`;
        } else {
          // For new emails or other types
          description = `Please help me with ${
            missingFieldsDescription || 'the missing mandatory fields'
          }. The following fields are missing: ${fieldList}. Please list the missing fields using bullets and format the message professionally.`;
        }

        const suggestions = await fetchAISuggestions({
          subject: form.subject,
          body: cleanBody,
          mode: 'refine',
          refine: {
            description,
            tone: 'Professional',
          },
        });
        setAISuggestions(suggestions);
      } else {
        const suggestions = await fetchAISuggestions({
          subject: form.subject,
          body: cleanBody,
          mode: 'initial',
        });
        setAISuggestions(suggestions);
      }
    } catch (error) {
      console.error('Error in handleAIAssist:', error);
      notifications.error(
        'Failed to generate AI suggestions. Please try again.'
      );
    } finally {
      setAILoading(false);
    }
  }, [
    form.subject,
    form.body,
    missingFields,
    missingFieldsDescription,
    fetchAISuggestions,
    notifications,
  ]);

  const handleUseText = (suggestion: string) => {
    // Convert newlines to <br> for HTML formatting
    const suggestionHtml = suggestion.replace(/\n/g, '<br>');
    const newBody = `${suggestionHtml}<br><br>${form.body}`;
    setForm((prev) => ({ ...prev, body: newBody }));

    setAIAssistOpen(false);
  };

  const handleAIModalClose = () => {
    setAIAssistOpen(false);
    setAILoading(false);
    setAISuggestions([]);
  };

  // Refine handler for AI suggestions
  const handleRefine = async () => {
    setAILoading(true);
    try {
      const suggestions = await fetchAISuggestions({
        subject: form.subject,
        body: form.body,
        mode: 'refine',
        refine: {
          description: aiRefineInput,
          tone: aiRefineTone,
        },
      });
      setAISuggestions(suggestions);
    } finally {
      setAILoading(false);
      setAIRefineMode(false);
    }
  };

  return {
    aiAssistOpen,
    setAIAssistOpen,
    aiLoading,
    setAILoading,
    aiSuggestions,
    setAISuggestions,
    aiRefineMode,
    setAIRefineMode,
    aiRefineInput,
    setAIRefineInput,
    aiRefineTone,
    setAIRefineTone,
    handleAIAssist,
    handleUseText,
    handleAIModalClose,
    handleRefine,
  };
};
